/**
 * Sätze zu abgelehnten oder unbeantworteten Anfragen der Ressourcen: ein bekannter Code bekommt
 * seinen Satz, ein unbekannter bleibt als Code sichtbar, ein Serverfehler nennt Code oder Status –
 * nie der interne Servertext.
 */
import type { ApiError } from '@/shared/api';

const KNOWN: Record<string, string> = {
  resource_name_duplicate: 'Dieses Objekt hat schon eine Ressource mit diesem Namen.',
  resource_name_invalid: 'Der Name fehlt oder ist länger als 80 Zeichen.',
  resource_opening_window_invalid:
    'Die Buchungszeit muss im 30-Minuten-Raster innerhalb eines Tages liegen, mit dem Beginn vor dem Ende.',
  resource_not_found: 'Die Ressource gibt es in diesem Objekt nicht mehr. Bitte lade neu.',
  object_not_found: 'Das Objekt wurde nicht gefunden.',
  object_access_denied: 'Ressourcen verwaltet nur, wer alle Apartments des Objekts verwalten darf.',
  resource_price_below_minimum:
    'Eine halbe Stunde muss mindestens den Mindestbetrag der Online-Zahlung erreichen.',
  resource_currency_unsupported:
    'In der Währung dieses Objekts sind nur kostenlose Ressourcen möglich.',
  resource_price_invalid: 'Der Preis fehlt oder hat mehr Nachkommastellen, als die Währung kennt.',
  resource_paid_cancellation_unconfirmed:
    'Die Reservierung wurde inzwischen bezahlt. Der Kalender ist aktualisiert – bitte erneut aufheben, dann ohne Erstattung.',
  resource_description_too_long: 'Eine Beschreibung hat höchstens 600 Zeichen.',
  resource_price_currency_mismatch:
    'Das Objekt rechnet inzwischen in einer anderen Währung. Bitte den Preis neu eingeben.',
  resource_slot_taken:
    'Der Zeitraum ist schon reserviert oder gesperrt. Der Stand wird neu geladen.',
  resource_slot_past: 'Diese Zeit hat schon begonnen oder ist vorbei. Wähle eine spätere Zeit.',
  resource_inactive: 'Die Ressource ist deaktiviert und lässt sich nicht sperren.',
  resource_outside_opening_hours: 'Die Zeit liegt außerhalb der Buchungszeit dieser Ressource.',
  resource_slot_time_change:
    'Die Zeit fällt in die Zeitumstellung und lässt sich an diesem Tag nicht sperren.',
  resource_slot_invalid: 'Die Zeit muss im 30-Minuten-Raster innerhalb eines Tages liegen.',
  resource_block_invalid: 'Der Grund fehlt oder die Notiz ist länger als 200 Zeichen.',
  resource_try_again: 'Gleichzeitig wurde dieselbe Zeit bearbeitet. Bitte versuche es noch einmal.',
  resource_reservation_not_found: 'Diese Belegung gibt es nicht mehr. Der Stand wird neu geladen.',
  resource_timezone_unknown:
    'Die Zeitzone des Objekts ist unbekannt – bitte in den Objektdaten prüfen.',
};

// Ein Code aus dem Server (`resource_slot_taken`) — im Gegensatz zu einem Framework- oder
// Proxy-Text ohne Problem-Body, der samt URL im Titel stünde.
const SERVER_CODE = /^[a-z][a-z0-9_]*$/;

function rejectionText(problem: ApiError, fallback: string): string {
  const known = KNOWN[problem.title];
  if (known) return known;
  if (problem.status >= 500) {
    const reason = SERVER_CODE.test(problem.title)
      ? `Code ${problem.title}`
      : `Status ${problem.status}`;
    return `${fallback} (${reason})`;
  }
  if (problem.status === 403) return 'Dafür fehlt dir die Berechtigung.';
  if (problem.status === 404) return 'Objekt oder Ressource wurden nicht gefunden. Bitte lade neu.';
  if (problem.fieldErrors || problem.status === 400) {
    return 'Die Angaben wurden nicht angenommen. Bitte prüfe die Eingaben.';
  }
  return SERVER_CODE.test(problem.title) ? `Fehler ${problem.title}` : fallback;
}

/** Satz für eine abgelehnte oder unbeantwortete Speicherung einer Ressource. */
export function resourceFailureText(problem: ApiError): string {
  if (problem.status === 0) {
    return 'Keine Antwort vom Server – ob gespeichert wurde, ist offen. Die Liste wird neu geladen.';
  }
  return rejectionText(problem, 'Die Ressource wurde nicht gespeichert. Bitte versuche es erneut.');
}

/** Satz zu Sperren oder Aufheben im Kalender – nicht „Die Ressource wurde nicht gespeichert“. */
export function occupancyFailureText(problem: ApiError, action: 'block' | 'cancel'): string {
  if (problem.status === 0) {
    const what = action === 'block' ? 'die Zeit gesperrt' : 'die Belegung aufgehoben';
    return `Keine Antwort vom Server – ob ${what} wurde, ist offen. Der Stand wird neu geladen.`;
  }
  return rejectionText(
    problem,
    action === 'block' ? 'Die Zeit wurde nicht gesperrt.' : 'Die Belegung wurde nicht aufgehoben.',
  );
}

/** Warum die Liste nicht geladen werden konnte — nie als „keine Ressourcen“ getarnt. */
export function resourceLoadFailureText(problem: ApiError): string {
  if (problem.status === 0) return 'Keine Verbindung zum Server.';
  return rejectionText(problem, 'Der Server konnte die Ressourcen gerade nicht liefern.');
}
